import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import OutsideClick from '../../../functions/OutsideClick';
import { readPreference, usePopoverPlacement, writePreference } from '../../../functions/popover';
import { firstUndrawableVersion, loadEmojiGrid, loadEmojiSkinSprite, loadEmojiSprite, loadEmojiWords, spriteKey } from '../../../emojis/emojiData';

import "./styles.scss"

const GROUPS = {
    recent: { icon: '🕘', name: 'Недавние' },
    smileys: { icon: '😀', name: 'Смайлики и эмоции' },
    people: { icon: '👋', name: 'Люди и тело' },
    nature: { icon: '🐻', name: 'Животные и природа' },
    food: { icon: '🍔', name: 'Еда и напитки' },
    travel: { icon: '🚗', name: 'Путешествия и места' },
    activities: { icon: '⚽', name: 'Занятия' },
    objects: { icon: '💡', name: 'Предметы' },
    symbols: { icon: '🔣', name: 'Символы' },
    flags: { icon: '🏁', name: 'Флаги' },
};
const SKIN_TONES = ['✋', '✋🏻', '✋🏼', '✋🏽', '✋🏾', '✋🏿'];
const MAX_RECENT = 32;
const PICKER_WIDTH = 352;
const PICKER_HEIGHT = 420;

const COLUMNS = 9;
const ROW_HEIGHT = 36;

const normalize = (text) => text.toLowerCase().replace(/ё/g, 'е');

// The picker's list is virtual: the sections are laid out as flat rows (a header, a row of COLUMNS emojis,
// a message) with known heights, and only the rows in or near the visible part of the list are made.
const HEADER_HEIGHT = 28;
const EMPTY_HEIGHT = 40;
const OVERSCAN_ROWS = 8;
const FALLBACK_VIEWPORT = 300;

function buildRows(sections) {
    const rows = [];
    const offsets = {};
    let top = 0;
    const push = (row, height) => {
        rows.push({ ...row, top, height });
        top += height;
    };
    sections.forEach((section) => {
        offsets[section.key] = top;
        push({ type: 'header', key: `${section.key}:header`, name: section.name }, HEADER_HEIGHT);
        if (section.emojis.length === 0) push({ type: 'empty', key: `${section.key}:empty`, text: section.empty }, EMPTY_HEIGHT);
        for (let i = 0; i < section.emojis.length; i += COLUMNS) {
            push({ type: 'emojis', key: `${section.key}:${i}`, entries: section.emojis.slice(i, i + COLUMNS) }, ROW_HEIGHT);
        }
    });
    return { rows, offsets, height: top };
}

// Index of the row containing the offset.
function rowAt(rows, offset) {
    let low = 0;
    let high = rows.length - 1;
    while (low < high) {
        const mid = (low + high + 1) >> 1;
        if (rows[mid].top <= offset) low = mid;
        else high = mid - 1;
    }
    return low;
}

// A row of the list. Memoized, so scrolling (which changes which rows are shown, not the rows) and the
// hover preview only make the rows that are new.
const GLYPH = 24;

// The emoji as an image from its sprite sheet, or as text when the sheet doesn't have it.
function Glyph({ emoji, sprite }) {
    const cell = sprite?.index.get(spriteKey(emoji));
    if (cell == null) return emoji;
    return (
        <span
            className="emoji-img"
            style={{
                backgroundImage: `url(${sprite.url})`,
                backgroundSize: `${sprite.columns * GLYPH}px ${sprite.rows * GLYPH}px`,
                backgroundPosition: `-${(cell % sprite.columns) * GLYPH}px -${Math.floor(cell / sprite.columns) * GLYPH}px`,
            }}
        />
    );
}

const EmojiRow = memo(function EmojiRow({ row, tone, sprite, onPick, onHover }) {
    let content;
    if (row.type === 'emojis') {
        content = row.entries.map((entry) => {
            const emoji = tone > 0 && entry.skins ? entry.skins[tone - 1] : entry.emoji;
            return (
            <button
                type="button"
                className="emoji-btn"
                key={entry.emoji}
                data-emoji={emoji}
                aria-label={emoji}
                onMouseDown={(e) => { e.preventDefault(); onPick(entry); }}
                onMouseEnter={() => onHover(entry)}
            >{sprite === undefined ? null : <Glyph emoji={emoji} sprite={sprite} />}</button>
            );
        });
    } else {
        content = row.type === 'header' ? row.name : row.text;
    }
    const className = row.type === 'emojis' ? 'emoji-group-emojis' : row.type === 'header' ? 'emoji-group-header' : 'emoji-empty';
    return <div className={className} style={{ top: row.top, height: row.height }}>{content}</div>;
});

function EmojiSelect({ addEmoji }) {
    const ref = useRef(null);
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [groups, setGroups] = useState(null);
    const [names, setNames] = useState(null);
    const [query, setQuery] = useState('');
    const [activeGroup, setActiveGroup] = useState(null);
    const [preview, setPreview] = useState(null);
    const [scrollTop, setScrollTop] = useState(0);
    // undefined while a sheet is loading, null when it can't be (the emojis are then drawn as text).
    const [sprite, setSprite] = useState(undefined);
    const [skinSprite, setSkinSprite] = useState(undefined);
    const [viewport, setViewport] = useState(FALLBACK_VIEWPORT);
    const [tone, setTone] = useState(() => readPreference('emojiSkinTone', 0));
    const [recent, setRecent] = useState(() => readPreference('recentEmojis', []));
    const { above, alignLeft } = usePopoverPlacement(ref, isOpen, PICKER_WIDTH, PICKER_HEIGHT);

    useEffect(() => {
        let cancelled = false;
        loadEmojiGrid().then(groups => {
            if (!cancelled) setGroups(groups);
        }).catch(err => console.log(err));

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        loadEmojiSprite().then(sprite => { if (!cancelled) setSprite(sprite); }).catch(() => { if (!cancelled) setSprite(null); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (tone === 0) return undefined;
        let cancelled = false;
        loadEmojiSkinSprite().then(sprite => { if (!cancelled) setSkinSprite(sprite); }).catch(() => { if (!cancelled) setSkinSprite(null); });
        return () => { cancelled = true; };
    }, [tone]);

    // The names and search words come once the picker is opened.
    useEffect(() => {
        if (!isOpen || names) return undefined;
        let cancelled = false;
        loadEmojiWords().then(names => {
            if (!cancelled) setNames(names);
        }).catch(err => console.log(err));

        return () => {
            cancelled = true;
        };
    }, [isOpen, names]);

    useEffect(() => {
        if (isOpen) searchRef.current?.focus();
    }, [isOpen, groups]);

    OutsideClick(ref, () => setIsOpen(false));

    // Drawable emojis by group, each as { emoji, skins }, plus a lookup by emoji for the recent ones.
    const { shown, byEmoji } = useMemo(() => {
        if (!groups || !isOpen) return { shown: null, byEmoji: null };
        const below = firstUndrawableVersion(groups);
        const byEmoji = new Map();
        const shown = groups.map(group => ({
            key: group.key,
            emojis: group.emojis
                .filter(([, version]) => version < below)
                .map(([emoji, , skins]) => {
                    const entry = { emoji, skins };
                    byEmoji.set(emoji, entry);
                    return entry;
                }),
        }));
        return { shown, byEmoji };
    }, [groups, isOpen]);

    // Each emoji's name, and the words it's found by, once they're in.
    const words = useMemo(() => names && new Map(Object.entries(names).map(
        ([emoji, [name, terms]]) => [emoji, { name, terms: normalize(`${name} ${terms}`) }],
    )), [names]);

    const sections = useMemo(() => {
        if (!shown) return [];
        const terms = normalize(query).split(/\s+/).filter(Boolean);
        if (terms.length > 0) {
            const found = words
                ? shown.flatMap(group => group.emojis).filter(entry => terms.every(term => words.get(entry.emoji)?.terms.includes(term)))
                : [];
            return [{ key: 'search', name: 'Результаты поиска', emojis: found, empty: words ? 'Ничего не найдено' : 'Загрузка…' }];
        }
        const recentEntries = recent.map(emoji => byEmoji.get(emoji)).filter(Boolean);
        return [
            ...(recentEntries.length > 0 ? [{ key: 'recent', emojis: recentEntries }] : []),
            ...shown,
        ].map(section => ({ ...section, name: GROUPS[section.key].name }));
    }, [shown, byEmoji, words, query, recent]);

    function toggle(e) {
        e.stopPropagation();
        e.preventDefault();

        setIsOpen(!isOpen);
        setQuery('');
    }

    const withTone = (entry) => (tone > 0 && entry.skins ? entry.skins[tone - 1] : entry.emoji);

    function pick(entry) {
        addEmoji(withTone(entry));
        const next = [entry.emoji, ...recent.filter(emoji => emoji !== entry.emoji)].slice(0, MAX_RECENT);
        setRecent(next);
        writePreference('recentEmojis', next);
    }

    function pickTone(index) {
        setTone(index);
        writePreference('emojiSkinTone', index);
    }

    const pickRef = useRef(pick);
    pickRef.current = pick;
    const onPick = useCallback((entry) => pickRef.current(entry), []);
    const onHover = useCallback((entry) => setPreview(entry), []);

    const layout = useMemo(() => buildRows(sections), [sections]);

    // A new search or a changed list starts at the top.
    useLayoutEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
        setScrollTop(0);
    }, [query]);

    useLayoutEffect(() => {
        if (isOpen && listRef.current) setViewport(listRef.current.clientHeight || FALLBACK_VIEWPORT);
    }, [isOpen, groups]);

    // Rows already shown stay mounted: tearing them down and making them again while scrolling was the jitter.
    // The range only grows until the list itself changes; the browser skips painting the rows out of view.
    const reached = useRef({ layout: null, first: 0, last: -1 });
    const visibleRows = useMemo(() => {
        const { rows } = layout;
        if (rows.length === 0) return [];
        const first = Math.max(0, rowAt(rows, scrollTop) - OVERSCAN_ROWS);
        const last = Math.min(rows.length - 1, rowAt(rows, scrollTop + viewport) + OVERSCAN_ROWS);
        const range = reached.current;
        if (range.layout !== layout) {
            reached.current = { layout, first, last };
        } else {
            range.first = Math.min(range.first, first);
            range.last = Math.max(range.last, last);
        }
        return rows.slice(reached.current.first, reached.current.last + 1);
    }, [layout, scrollTop, viewport]);

    const scrollTo = (key) => {
        const offset = layout.offsets[key];
        if (offset != null) {
            listRef.current.scrollTop = offset;
            setScrollTop(offset);
        }
        setActiveGroup(key);
    };

    const onScroll = (e) => {
        const top = e.currentTarget.scrollTop;
        setScrollTop(Math.floor(top / ROW_HEIGHT) * ROW_HEIGHT);
        const current = sections.filter(section => layout.offsets[section.key] <= top + 1).pop();
        if (current) setActiveGroup(current.key);
    };

    const onSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const first = sections[0]?.emojis[0];
            if (first) pick(first);
        } else if (e.key === 'Escape') {
            e.stopPropagation();
            setIsOpen(false);
        }
    };

    const picker = (
        <div
            className={classNames('emoji-picker', { 'emoji-picker_above': above, 'emoji-picker_align-left': alignLeft })}
            onMouseDown={(e) => { if (e.target !== searchRef.current) e.preventDefault(); }}
        >
            <input
                ref={searchRef}
                className="emoji-search"
                type="search"
                placeholder="Поиск эмодзи"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
            />
            {!query && <div className="emoji-groups">
                {sections.map(section => (
                    <button
                        type="button"
                        key={section.key}
                        title={section.name}
                        className={classNames('emoji-groups-btn', { 'emoji-groups-btn_active': (activeGroup ?? sections[0]?.key) === section.key })}
                        onMouseDown={(e) => { e.preventDefault(); scrollTo(section.key); }}
                    >
                        {GROUPS[section.key].icon}
                    </button>
                ))}
            </div>}
            <div className="emoji-list" ref={listRef} onScroll={onScroll}>
                {!shown && <div className="emoji-empty">Загрузка…</div>}
                <div className="emoji-rows" style={{ height: layout.height }}>
                    {visibleRows.map(row => (
                        <EmojiRow key={row.key} row={row} tone={tone} sprite={tone > 0 ? skinSprite : sprite} onPick={onPick} onHover={onHover} />
                    ))}
                </div>
            </div>
            <div className="emoji-footer">
                <span className="emoji-preview">{preview && withTone(preview)}</span>
                <span className="emoji-preview-name">{preview && words?.get(preview.emoji)?.name}</span>
                <span className="emoji-tones" title="Тон кожи">
                    {SKIN_TONES.map((emoji, index) => (
                        <button
                            type="button"
                            key={emoji}
                            className={classNames('emoji-tone', { 'emoji-tone_active': tone === index })}
                            onMouseDown={(e) => { e.preventDefault(); pickTone(index); }}
                        >{emoji}</button>
                    ))}
                </span>
            </div>
        </div>
    );

    return (
        <div className="emoji-select" ref={ref}>
            <button type="button" className="emoji-header" title="Эмодзи" onMouseDown={toggle}>&#128515;</button>

            <div className="emoji-select-container">
                {isOpen && picker}
            </div>
        </div>
    );
}

export default EmojiSelect;
